import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Server, Cpu, MemoryStick, HardDrive, Activity,
  AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw, ArrowUp, ArrowDown,
} from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { safeJsonFetch, authHeaders } from '../lib/safeFetch';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20_000;
const HISTORY_LEN = 12;  // 4 minutes of 20-s samples

/** Static Tailwind color maps — dynamic interpolation gets purged. */
const STATUS_THEME = {
  ok: {
    ring: 'stroke-emerald-500',
    fill: 'fill-emerald-500',
    text: 'text-emerald-700',
    bg: 'bg-emerald-50',
    bgBorder: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-800',
    dot: 'bg-emerald-500',
    glow: 'shadow-emerald-200/40',
    border: 'border-emerald-200',
    gradient: 'from-emerald-50 to-white',
    sparkline: '#10b981',
  },
  warn: {
    ring: 'stroke-amber-500',
    fill: 'fill-amber-500',
    text: 'text-amber-700',
    bg: 'bg-amber-50',
    bgBorder: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-800',
    dot: 'bg-amber-500',
    glow: 'shadow-amber-200/40',
    border: 'border-amber-200',
    gradient: 'from-amber-50 to-white',
    sparkline: '#f59e0b',
  },
  critical: {
    ring: 'stroke-rose-500',
    fill: 'fill-rose-500',
    text: 'text-rose-700',
    bg: 'bg-rose-50',
    bgBorder: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-800',
    dot: 'bg-rose-500',
    glow: 'shadow-rose-300/50',
    border: 'border-rose-200',
    gradient: 'from-rose-50 to-white',
    sparkline: '#ef4444',
  },
};

const STATUS_LABEL = {
  ok: 'All systems healthy',
  warn: 'Approaching limit',
  critical: 'Action required',
};

const ServerResourcesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const historyRef = useRef({ cpu: [], memory: [] });
  const [, forceRender] = useState(0);

  const fetchSnapshot = useCallback(async () => {
    const res = await safeJsonFetch(`${API}/api/admin/system/resources`, { headers: authHeaders() });
    if (res.ok && res.data) {
      setData(res.data);
      // Append to rolling history for sparklines
      const h = historyRef.current;
      h.cpu = [...h.cpu, res.data.cpu?.percent ?? 0].slice(-HISTORY_LEN);
      h.memory = [...h.memory, res.data.memory?.percent ?? 0].slice(-HISTORY_LEN);
      forceRender((x) => x + 1);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSnapshot();
    const id = setInterval(fetchSnapshot, POLL_MS);
    return () => clearInterval(id);
  }, [fetchSnapshot]);

  if (loading || !data) {
    return (
      <Card className="p-6 border-0 shadow-md bg-gradient-to-br from-slate-50 to-white" data-testid="server-resources-card">
        <div className="flex items-center gap-2 text-gray-500">
          <Server className="w-4 h-4 animate-pulse" /> Loading server resources...
        </div>
      </Card>
    );
  }

  if (data.error) {
    return (
      <Card className="p-6 border-0 shadow-md border-l-4 border-l-amber-400" data-testid="server-resources-card">
        <p className="text-sm text-amber-800">Server resources unavailable: {data.error}</p>
      </Card>
    );
  }

  const status = data.overall_status || 'ok';
  const theme = STATUS_THEME[status];
  const StatusIcon = status === 'ok' ? CheckCircle2 : AlertTriangle;

  return (
    <Card
      className={`relative overflow-hidden border-0 shadow-xl ${theme.glow} shadow-2xl bg-gradient-to-br ${theme.gradient}`}
      data-testid="server-resources-card"
    >
      {/* Decorative top-left gradient accent */}
      <div className={`absolute -top-20 -left-20 w-60 h-60 rounded-full opacity-10 ${theme.dot} blur-3xl pointer-events-none`} />

      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`relative w-10 h-10 rounded-lg ${theme.bg} flex items-center justify-center`}>
              <Server className={`w-5 h-5 ${theme.text}`} />
              <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${theme.dot} ring-2 ring-white ${status !== 'ok' ? 'animate-pulse' : ''}`} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-base leading-tight">Server Resources</h3>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge className={`${theme.badge} border-0 text-[10px] font-semibold uppercase tracking-wider`}>
                  <StatusIcon className="w-3 h-3 mr-1 inline" /> {STATUS_LABEL[status]}
                </Badge>
                <span className="text-[10px] text-gray-400">live · updates every 20s</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={fetchSnapshot} title="Refresh now" className="h-8 w-8 p-0" data-testid="server-resources-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="h-8 w-8 p-0" data-testid="server-resources-expand-btn">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>

        {/* Hero gauges — CPU + Memory side by side */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <GaugeTile
            icon={Cpu}
            label="CPU"
            percent={data.cpu.percent}
            status={data.cpu.status}
            primary={`${data.cpu.percent.toFixed(1)}%`}
            footnoteLeft={`${data.cpu.cores} cores`}
            footnoteRight={`load ${data.cpu.load_avg_1m.toFixed(2)}`}
            history={historyRef.current.cpu}
            testId="resource-cpu"
          />
          <GaugeTile
            icon={MemoryStick}
            label="Memory"
            percent={data.memory.percent}
            status={data.memory.status}
            primary={data.memory.used}
            primaryUnit={`/ ${data.memory.total}`}
            footnoteLeft={data.memory.source === 'cgroup' ? 'container limit' : 'host limit'}
            footnoteRight={`${data.memory.available} free`}
            history={historyRef.current.memory}
            testId="resource-memory"
          />
        </div>

        {/* Status callout — surfaces the recommended action */}
        {status !== 'ok' && (
          <div className={`mt-4 p-3 rounded-lg ${theme.bg} ${theme.bgBorder} border flex items-start gap-3`} data-testid="server-resources-action">
            <AlertTriangle className={`w-4 h-4 ${theme.text} shrink-0 mt-0.5`} />
            <div className="text-xs">
              <p className={`font-semibold mb-0.5 ${theme.text}`}>
                {status === 'critical' ? 'Resource ceiling approached' : 'Resource usage elevated'}
              </p>
              <p className={`${theme.text} opacity-80`}>
                {status === 'critical'
                  ? 'Large parcel imports may be silently killed by the container orchestrator. Consider upgrading the Emergent deploy tier — share this dashboard screenshot with Emergent Support.'
                  : 'Usage is elevated. If you plan to import a large county shapefile (200 MB+), watch this card or wait until usage subsides.'}
              </p>
            </div>
          </div>
        )}

        {/* Expanded drilldown */}
        {expanded && (
          <div className="mt-5 pt-5 border-t border-gray-200 space-y-5" data-testid="server-resources-expanded">
            {/* Disk donuts */}
            <section>
              <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
                <HardDrive className="w-3 h-3" /> Disk mounts
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(data.disk || []).map((d) => (
                  <DiskDonut key={d.path} d={d} testId={`resource-disk-${d.path.replace(/[^a-z0-9]+/gi, '-')}`} />
                ))}
              </div>
            </section>

            {/* Per-tool ephemeral storage */}
            {data.tool_storage && data.tool_storage.length > 0 && (
              <section>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
                  <Activity className="w-3 h-3" /> Storage by tool (ephemeral /tmp)
                </h4>
                <ToolStorageBars items={data.tool_storage} />
              </section>
            )}

            {/* Process info — small bottom strip */}
            {data.process && (
              <section className="flex items-center justify-between text-[11px] text-gray-500 font-mono pt-1">
                <span>Python worker</span>
                <span>
                  RSS <span className="text-gray-900 font-semibold">{data.process.rss}</span>
                  {' · '}{data.process.threads} threads
                  {' · '}{data.process.open_files} open files
                </span>
              </section>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

/* ----- Radial gauge tile ----- */
const GaugeTile = ({ icon: Icon, label, percent, status, primary, primaryUnit, footnoteLeft, footnoteRight, history, testId }) => {
  const theme = STATUS_THEME[status] || STATUS_THEME.ok;
  const safePct = Math.min(Math.max(percent || 0, 0), 100);

  // Compute trend arrow from history
  const trend = useMemo(() => {
    if (!history || history.length < 2) return null;
    const last = history[history.length - 1];
    const prev = history[history.length - 2];
    const delta = last - prev;
    if (Math.abs(delta) < 0.5) return null;
    return delta > 0 ? 'up' : 'down';
  }, [history]);

  return (
    <div className="bg-white/70 backdrop-blur rounded-xl border border-gray-200 p-4 transition-shadow hover:shadow-md" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5 text-gray-500" />}
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">{label}</span>
        </div>
        {trend && (
          <span className={`flex items-center gap-0.5 text-[10px] font-semibold ${trend === 'up' ? theme.text : 'text-gray-400'}`}>
            {trend === 'up' ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
            {trend === 'up' ? 'rising' : 'easing'}
          </span>
        )}
      </div>

      <div className="flex items-center gap-4">
        <RadialGauge percent={safePct} theme={theme} />
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-gray-900 leading-tight truncate">
            {primary}
            {primaryUnit && <span className="text-sm font-medium text-gray-500 ml-1">{primaryUnit}</span>}
          </p>
          <div className="flex items-center justify-between mt-1 text-[10px] text-gray-500">
            <span className="truncate">{footnoteLeft}</span>
            <span className="truncate ml-2">{footnoteRight}</span>
          </div>
        </div>
      </div>

      {history && history.length >= 2 && (
        <div className="mt-3 -mx-1">
          <Sparkline values={history} color={theme.sparkline} />
        </div>
      )}
    </div>
  );
};

/* ----- SVG radial gauge ----- */
const RadialGauge = ({ percent, theme, size = 76, stroke = 8 }) => {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  // Three-quarter arc starting at 135° so the open gap is at the bottom
  const arcRatio = 0.75;
  const arcLen = circ * arcRatio;
  const offset = arcLen * (1 - percent / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
      <g transform={`rotate(135 ${size / 2} ${size / 2})`}>
        {/* track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgb(229,231,235)"  /* gray-200 */
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arcLen} ${circ}`}
        />
        {/* active arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className={`${theme.ring} transition-[stroke-dashoffset] duration-700 ease-out`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${arcLen} ${circ}`}
          strokeDashoffset={offset}
        />
      </g>
      <text
        x="50%"
        y="50%"
        dy="0.35em"
        textAnchor="middle"
        className={`text-base font-bold ${theme.text} tabular-nums`}
        style={{ fontSize: '15px' }}
      >
        {percent.toFixed(0)}%
      </text>
    </svg>
  );
};

/* ----- Tiny SVG sparkline ----- */
const Sparkline = ({ values, color, width = 200, height = 28 }) => {
  const max = Math.max(100, ...values);
  const min = 0;
  const range = max - min || 1;
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((v, i) => {
    const x = i * step;
    const y = height - ((v - min) / range) * height;
    return `${x},${y.toFixed(1)}`;
  });
  // Build a closed area path for a subtle fill below the line
  const linePath = `M ${points.join(' L ')}`;
  const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-7" aria-hidden="true">
      <path d={areaPath} fill={color} opacity={0.12} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

/* ----- Disk usage donut ----- */
const DiskDonut = ({ d, testId }) => {
  const theme = STATUS_THEME[d.status] || STATUS_THEME.ok;
  const size = 84;
  const stroke = 9;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = (d.percent / 100) * circ;

  return (
    <div className="flex items-center gap-3 bg-white/70 backdrop-blur rounded-xl border border-gray-200 p-3" data-testid={testId}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgb(229,231,235)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          className={`${theme.ring} transition-[stroke-dasharray] duration-700`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
        <text x="50%" y="50%" dy="0.35em" textAnchor="middle" className={`font-bold ${theme.text}`} style={{ fontSize: '14px' }}>
          {d.percent.toFixed(0)}%
        </text>
      </svg>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-gray-900 truncate">{d.label}</p>
        <p className="text-[10px] text-gray-500 font-mono truncate" title={d.path}>{d.path}</p>
        <p className="text-[11px] text-gray-700 mt-0.5">
          <span className="font-semibold">{d.used}</span> / {d.total}
          <span className="text-gray-400"> · {d.free} free</span>
        </p>
      </div>
    </div>
  );
};

/* ----- Tool storage stacked bars ----- */
const ToolStorageBars = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.size_bytes || 0));
  return (
    <div className="space-y-1.5">
      {items.map((t) => {
        const pct = max ? Math.max(((t.size_bytes || 0) / max) * 100, 0.5) : 0;
        return (
          <div
            key={t.path}
            className="flex items-center gap-3 text-[11px] text-gray-700 bg-white/70 backdrop-blur border border-gray-200 rounded-lg px-3 py-2"
            data-testid={`resource-tool-${t.path.replace(/[^a-z0-9]+/gi, '-')}`}
          >
            <span className="w-44 truncate font-medium">{t.label}</span>
            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-400 to-violet-500 rounded-full transition-[width] duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="font-mono font-semibold w-16 text-right tabular-nums">{t.size}</span>
          </div>
        );
      })}
    </div>
  );
};

export default ServerResourcesCard;
